// Documentos de inventario: recepciones, transferencias y ajustes. Permite anular
// (reversa) un documento confirmado. Inventario → Operaciones → Documentos.
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Eye, FileStack, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import {
  Campo, Location, PartnerOpt, ProductoOpt, fieldCls, inv, invRpc, invalidateInventario,
  locationsList, money, num, partnersList, productosOptList,
} from './shared';
import { DocumentoImprimible, DocColumn, DocMeta } from './DocumentoImprimible';
import { LOCALE } from '@/lib/utils/regional';

type PrintDoc = { titulo: string; codigo: string; meta: DocMeta[]; columns: DocColumn[]; filas: ReactNode[][]; totalLabel?: string; totalValue?: ReactNode };
interface LineaDoc { producto_id: string; descripcion?: string | null; cantidad: number; costo_unitario?: number; numero_lote?: string | null; fecha_caducidad?: string | null }

type Tipo = 'recepcion' | 'transferencia' | 'ajuste' | 'venta' | 'nota_credito';
type TipoInv = 'recepcion' | 'transferencia' | 'ajuste';
const TABS: { tipo: Tipo; label: string; tabla: string }[] = [
  { tipo: 'recepcion', label: 'Recepciones', tabla: 'recepciones' },
  { tipo: 'transferencia', label: 'Transferencias', tabla: 'transferencias' },
  { tipo: 'ajuste', label: 'Ajustes', tabla: 'ajustes' },
  { tipo: 'venta', label: 'Ventas', tabla: '' },
  { tipo: 'nota_credito', label: 'Notas de crédito', tabla: '' },
];
const esPos = (t: Tipo): t is 'venta' | 'nota_credito' => t === 'venta' || t === 'nota_credito';

interface Doc {
  id: string; codigo: string | null; fecha: string; estado: string;
  total_lineas: number; total_costo: number;
  referencia?: string | null; clase?: string | null; motivo?: string | null; observaciones?: string | null;
  partner_id?: string | null;
  location_id?: string | null; location_origen_id?: string | null; location_destino_id?: string | null;
  recepcion?: { codigo: string | null; referencia: string | null } | null; // origen (trazabilidad de transferencia precargada)
}

const SELECTS: Record<TipoInv, string> = {
  recepcion: 'id,codigo,fecha,referencia,estado,total_lineas,total_costo,location_id,partner_id',
  transferencia: 'id,codigo,fecha,estado,total_lineas,total_costo,location_origen_id,location_destino_id,observaciones,recepcion:recepcion_id(codigo,referencia)',
  ajuste: 'id,codigo,fecha,clase,motivo,estado,total_lineas,total_costo,location_id',
};

const LINEAS_TABLA: Record<TipoInv, { tabla: string; fk: string; select: string }> = {
  recepcion: { tabla: 'recepcion_lineas', fk: 'recepcion_id', select: 'producto_id,numero_lote,fecha_caducidad,cantidad,costo_unitario' },
  transferencia: { tabla: 'transferencia_lineas', fk: 'transferencia_id', select: 'producto_id,cantidad' },
  ajuste: { tabla: 'ajuste_lineas', fk: 'ajuste_id', select: 'producto_id,numero_lote,cantidad,costo_unitario' },
};

const estadoBadge = (e: string) => {
  const cls = (e === 'confirmado' || e === 'registrada' || e === 'emitida') ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
    : (e === 'anulado' || e === 'anulada') ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
    : 'bg-muted text-muted-foreground';
  return <span className={`rounded-full px-2 py-0.5 text-xs ${cls}`}>{e}</span>;
};

export function Documentos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [tipo, setTipo] = useState<Tipo>('recepcion');
  const tabla = TABS.find((t) => t.tipo === tipo)!.tabla;

  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const { data: productos = [] } = useQuery<ProductoOpt[]>({ queryKey: ['inv-productos-opt'], queryFn: productosOptList });
  const { data: partners = [] } = useQuery<PartnerOpt[]>({ queryKey: ['fin-partners'], queryFn: partnersList });
  const locName = (id?: string | null) => bodegas.find((b) => b.id === id)?.nombre ?? '—';
  const prodLabel = (id: string) => { const p = productos.find((x) => x.id === id); return p ? `${p.codigo} · ${p.nombre}` : id; };

  const [printDoc, setPrintDoc] = useState<PrintDoc | null>(null);

  // Filtros (fecha y bodega → server-side; número → sobre el resultado)
  const [fdesde, setFdesde] = useState('');
  const [fhasta, setFhasta] = useState('');
  const [fbodega, setFbodega] = useState('');
  const [fnumero, setFnumero] = useState('');

  const { data: docs = [], isFetching } = useQuery<Doc[]>({
    queryKey: ['inv-docs', tipo, fdesde, fhasta, fbodega],
    queryFn: () => {
      if (esPos(tipo)) {
        return invRpc<Doc[]>('inv_docs_pos', { _tipo: tipo, _desde: fdesde || null, _hasta: fhasta || null, _bodega: fbodega || null });
      }
      const parts = [`select=${SELECTS[tipo as TipoInv]}`];
      if (fdesde) parts.push(`fecha=gte.${fdesde}`);
      if (fhasta) parts.push(`fecha=lte.${fhasta}`);
      if (fbodega) parts.push(tipo === 'transferencia'
        ? `or=(location_origen_id.eq.${fbodega},location_destino_id.eq.${fbodega})`
        : `location_id=eq.${fbodega}`);
      return inv.list<Doc>(tabla, `?${parts.join('&')}&order=created_at.desc&limit=200`);
    },
  });

  const filtered = useMemo(() => {
    const s = fnumero.trim().toLowerCase();
    return s ? docs.filter((d) => (d.codigo ?? '').toLowerCase().includes(s)) : docs;
  }, [docs, fnumero]);

  const anular = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
      invRpc<{ codigo: string; movimientos_revertidos: number }>('inv_documento_anular', { p_tipo: tipo, p_id: id, p_motivo: motivo }),
    onSuccess: (r) => {
      toast.success(`${r.codigo} anulado · ${r.movimientos_revertidos} movimientos revertidos`);
      invalidateInventario(qc);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const onAnular = async (d: Doc) => {
    const v = await dlg.pedir(`Anular ${d.codigo}`, [{ key: 'motivo', label: 'Motivo', minimo: 0 }], {
      detalle: 'Esto revierte los movimientos de stock.', aceptar: 'Anular',
    });
    if (v === null) return;
    anular.mutate({ id: d.id, motivo: v.motivo });
  };

  const buildDoc = (d: Doc, lns: LineaDoc[]): PrintDoc => {
    if (esPos(tipo)) {
      const esNc = tipo === 'nota_credito';
      return {
        titulo: esNc ? 'Nota de crédito (devolución)' : 'Venta', codigo: d.codigo ?? '',
        meta: [
          { label: 'Fecha', value: new Date(d.fecha).toLocaleString(LOCALE) },
          { label: 'Bodega', value: locName(d.location_id) },
          { label: esNc ? 'Cliente / motivo' : 'Cliente', value: d.referencia || '—' },
        ],
        columns: [{ header: 'Producto' }, { header: 'Cantidad', right: true }, { header: 'Precio unit.', right: true }],
        filas: lns.map((l) => [l.descripcion || prodLabel(l.producto_id), num(l.cantidad, 2), l.costo_unitario != null ? money(l.costo_unitario) : '—']),
        totalLabel: 'Total', totalValue: money(d.total_costo),
      };
    }
    if (tipo === 'transferencia') {
      return {
        titulo: 'Transferencia entre bodegas', codigo: d.codigo ?? '',
        meta: [
          { label: 'Fecha', value: d.fecha },
          { label: 'Origen', value: locName(d.location_origen_id) },
          { label: 'Destino', value: locName(d.location_destino_id) },
          { label: 'Observaciones', value: d.observaciones || '—' },
        ],
        columns: [{ header: 'Producto' }, { header: 'Cantidad', right: true }],
        filas: lns.map((l) => [prodLabel(l.producto_id), num(l.cantidad, 2)]),
        totalLabel: 'Total unidades', totalValue: num(lns.reduce((s, l) => s + Number(l.cantidad || 0), 0), 2),
      };
    }
    if (tipo === 'ajuste') {
      return {
        titulo: d.clase === 'inventario_inicial' ? 'Carga inicial de inventario' : 'Ajuste de inventario', codigo: d.codigo ?? '',
        meta: [
          { label: 'Fecha', value: d.fecha },
          { label: 'Bodega', value: locName(d.location_id) },
          { label: 'Motivo', value: d.motivo || '—' },
        ],
        columns: [{ header: 'Producto' }, { header: 'Cantidad', right: true }, { header: 'Costo unit.', right: true }, { header: 'Lote' }],
        filas: lns.map((l) => [prodLabel(l.producto_id), num(l.cantidad, 2), l.costo_unitario != null ? money(l.costo_unitario) : '—', l.numero_lote || '—']),
      };
    }
    const partnerName = partners.find((p) => p.id === d.partner_id)?.razon_social ?? '—';
    return {
      titulo: 'Recepción de mercadería', codigo: d.codigo ?? '',
      meta: [
        { label: 'Fecha', value: d.fecha },
        { label: 'Bodega', value: locName(d.location_id) },
        { label: 'Proveedor', value: partnerName },
        { label: 'Referencia', value: d.referencia || '—' },
      ],
      columns: [{ header: 'Producto' }, { header: 'Cantidad', right: true }, { header: 'Costo unit.', right: true }, { header: 'Lote' }, { header: 'Caducidad' }],
      filas: lns.map((l) => [prodLabel(l.producto_id), num(l.cantidad, 2), l.costo_unitario != null ? money(l.costo_unitario) : '—', l.numero_lote || '—', l.fecha_caducidad || '—']),
      totalLabel: 'Total', totalValue: money(d.total_costo),
    };
  };

  const ver = useMutation({
    mutationFn: async (d: Doc) => {
      if (esPos(tipo)) {
        const lns = await invRpc<LineaDoc[]>('inv_docs_pos_lineas', { _tipo: tipo, _doc_id: d.id });
        return buildDoc(d, lns);
      }
      const cfg = LINEAS_TABLA[tipo as TipoInv];
      const lns = await inv.list<LineaDoc>(cfg.tabla, `?select=${cfg.select}&${cfg.fk}=eq.${d.id}&order=created_at`);
      return buildDoc(d, lns);
    },
    onSuccess: (doc) => setPrintDoc(doc),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const titulo = useMemo(() => TABS.find((t) => t.tipo === tipo)!.label, [tipo]);

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <h1 className="text-xl font-semibold flex items-center gap-2"><FileStack className="w-5 h-5" /> Documentos de inventario</h1>

      <div className="flex gap-2">
        {TABS.map((t) => (
          <button key={t.tipo} onClick={() => setTipo(t.tipo)}
            className={`rounded-md px-3 py-1.5 text-sm ${tipo === t.tipo ? 'bg-primary text-primary-foreground' : 'bg-muted hover:bg-muted/70'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-4">
          <Campo label="Bodega">
            <select className={fieldCls} value={fbodega} onChange={(e) => setFbodega(e.target.value)}>
              <option value="">— Todas —</option>
              {bodegas.map((b) => <option key={b.id} value={b.id}>{b.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Número"><input className={fieldCls} placeholder="REC-000001…" value={fnumero} onChange={(e) => setFnumero(e.target.value)} /></Campo>
          <Campo label="Desde"><input type="date" className={fieldCls} value={fdesde} onChange={(e) => setFdesde(e.target.value)} /></Campo>
          <Campo label="Hasta"><input type="date" className={fieldCls} value={fhasta} onChange={(e) => setFhasta(e.target.value)} /></Campo>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{titulo} ({filtered.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Fecha</th>
              {tipo === 'transferencia'
                ? <th className="py-2 pr-3">Origen → Destino</th>
                : <th className="py-2 pr-3">Bodega</th>}
              <th className="py-2 pr-3">{esPos(tipo) ? 'Cliente' : tipo === 'ajuste' ? 'Clase / motivo' : tipo === 'transferencia' ? 'Doc. referencia' : 'Referencia'}</th>
              <th className="py-2 pr-3 text-right">Líneas</th><th className="py-2 pr-3 text-right">{esPos(tipo) ? 'Total' : 'Costo'}</th>
              <th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-muted-foreground">Sin documentos</td></tr>}
              {filtered.map((d) => (
                <tr key={d.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono text-xs">{d.codigo}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{esPos(tipo) ? new Date(d.fecha).toLocaleString(LOCALE) : d.fecha}</td>
                  <td className="py-2 pr-3">
                    {tipo === 'transferencia'
                      ? <>{locName(d.location_origen_id)} → {locName(d.location_destino_id)}</>
                      : locName(d.location_id)}
                  </td>
                  <td className="py-2 pr-3 text-muted-foreground">
                    {tipo === 'ajuste'
                      ? <>{d.clase === 'inventario_inicial' ? 'Carga inicial' : 'Ajuste'}{d.motivo ? ` · ${d.motivo}` : ''}</>
                      : tipo === 'transferencia'
                        ? (d.recepcion ? `${d.recepcion.codigo ?? ''}${d.recepcion.referencia ? ` · ${d.recepcion.referencia}` : ''}` : '—')
                        : (d.referencia ?? '—')}
                  </td>
                  <td className="py-2 pr-3 text-right">{d.total_lineas}</td>
                  <td className="py-2 pr-3 text-right">{money(d.total_costo)}</td>
                  <td className="py-2 pr-3">{estadoBadge(d.estado)}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs hover:bg-muted"
                      title="Ver / imprimir" disabled={ver.isPending} onClick={() => ver.mutate(d)}>
                      <Eye className="w-3.5 h-3.5" /> Ver
                    </button>
                    {!esPos(tipo) && d.estado === 'confirmado' && (
                      <button className="ml-1 inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                        title="Anular (revertir movimientos)" onClick={() => onAnular(d)}>
                        <Ban className="w-3.5 h-3.5" /> Anular
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {printDoc && <DocumentoImprimible {...printDoc} onClose={() => setPrintDoc(null)} />}
    </div>
  );
}
